import { SettingOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ColorPicker, Popover } from "antd";
import { RootState } from "../../../../stores/Store";
import { useNotificationSocket } from "../../../../hooks/useNotifications";
import { NotificationBell } from "../notification/NotificationBell";
import { FloatingChat } from "../chat/FloatingChat";
import { MessagesButtonHeader } from "../chat/MessagesButtonHeader";
import { UserAccountMenu } from "../account/UserAccountMenu";

const HEADER_BACKGROUND_STORAGE_KEY = "adc_header_background_color";
const HEADER_TEXT_STORAGE_KEY = "adc_header_text_color";
const HEADER_LOGO_STORAGE_KEY = "adc_header_logo_color";
const DEFAULT_HEADER_BACKGROUND_COLOR = "#001e5a";
const DEFAULT_HEADER_TEXT_COLOR = "#ffffff";
const DEFAULT_HEADER_LOGO_COLOR = "#ffffff";

export const Header = () => {
  const authUserId = useSelector((state: RootState) => state.auth.userId);
  useNotificationSocket(!!authUserId);
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState(() => {
    try {
      return localStorage.getItem(HEADER_BACKGROUND_STORAGE_KEY) || DEFAULT_HEADER_BACKGROUND_COLOR;
    } catch {
      return DEFAULT_HEADER_BACKGROUND_COLOR;
    }
  });
  const [headerTextColor, setHeaderTextColor] = useState(() => {
    try {
      return localStorage.getItem(HEADER_TEXT_STORAGE_KEY) || DEFAULT_HEADER_TEXT_COLOR;
    } catch {
      return DEFAULT_HEADER_TEXT_COLOR;
    }
  });
  const [headerLogoColor, setHeaderLogoColor] = useState(() => {
    try {
      return localStorage.getItem(HEADER_LOGO_STORAGE_KEY) || DEFAULT_HEADER_LOGO_COLOR;
    } catch {
      return DEFAULT_HEADER_LOGO_COLOR;
    }
  });
  const skipHeaderColorStorageWrite = useRef(false);

  useEffect(() => {
    if (skipHeaderColorStorageWrite.current) {
      skipHeaderColorStorageWrite.current = false;
      return;
    }
    try {
      localStorage.setItem(HEADER_BACKGROUND_STORAGE_KEY, headerBackgroundColor);
      localStorage.setItem(HEADER_TEXT_STORAGE_KEY, headerTextColor);
      localStorage.setItem(HEADER_LOGO_STORAGE_KEY, headerLogoColor);
    } catch {}
  }, [headerBackgroundColor, headerTextColor, headerLogoColor]);

  const clearLocalStorage = useCallback(() => {
    localStorage.clear();
    window.location.reload();
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 w-full shadow-md" style={{ backgroundColor: headerBackgroundColor, color: headerTextColor }}>
        <div className="h-[50px] px-8 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a href="/" className="flex items-center gap-3">
              <span
                role="img"
                aria-label="ADC logo"
                className="inline-block h-10 w-[84px] shrink-0 transition-transform duration-200 hover:scale-105"
                style={{
                  backgroundColor: headerLogoColor,
                  WebkitMaskImage: "url('/adc_logo.svg')",
                  WebkitMaskPosition: "center",
                  WebkitMaskRepeat: "no-repeat",
                  WebkitMaskSize: "contain",
                  maskImage: "url('/adc_logo.svg')",
                  maskPosition: "center",
                  maskRepeat: "no-repeat",
                  maskSize: "contain",
                }}
              />
            </a>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <NotificationBell tone="light" />
            <MessagesButtonHeader tone="light" />
            <Popover
              trigger="click"
              placement="bottomRight"
              content={
                <div className="w-[185px] space-y-3 py-1">
                  <div className="flex items-center justify-between gap-3 text-xs text-slate-700">
                    <span>Màu nền header</span>
                    <ColorPicker value={headerBackgroundColor} onChange={(_, css) => setHeaderBackgroundColor(css)} showText />
                  </div>
                  <div className="flex items-center justify-between gap-3 text-xs text-slate-700">
                    <span>Màu chữ header</span>
                    <ColorPicker value={headerTextColor} onChange={(_, css) => setHeaderTextColor(css)} showText />
                  </div>
                  <div className="flex items-center justify-between gap-3 text-xs text-slate-700">
                    <span>Màu logo</span>
                    <ColorPicker value={headerLogoColor} onChange={(_, css) => setHeaderLogoColor(css)} showText />
                  </div>
                  <button
                    type="button"
                    onClick={clearLocalStorage}
                    className="w-full border-t border-slate-200 pt-2 text-left text-xs font-medium text-slate-600 transition-colors hover:text-blue-600 border-none outline-none cursor-pointer rounded-none bg-transparent"
                  >
                    Khôi phục mặc định hệ thống
                  </button>
                </div>
              }
            >
              <button
                type="button"
                title="Tùy chỉnh màu header"
                className="box-border inline-flex h-[26px] w-[26px] min-w-[26px] max-w-[26px] items-center justify-center p-0 shrink-0 border-none bg-white/10 text-white hover:bg-white/20 rounded-none cursor-pointer outline-none transition-colors"
                style={{ color: headerTextColor }}
              >
                <SettingOutlined className="text-[16px]" />
              </button>
            </Popover>
            <UserAccountMenu
              triggerStyle={{ color: headerTextColor }}
              triggerClassName="h-[28px] w-[28px] rounded-full bg-white/10 transition-all hover:bg-white/20 hover:ring-2 hover:ring-white/20"
            />
          </div>
        </div>
      </header>
      <div className="h-[50px]" />
      <FloatingChat />
    </>
  );
};
